import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { EmsStatusService } from '@/features/telemetry/services/EmsStatusService';
import {
  TelemetryService,
  type TelemetryQueryParams,
} from '@/features/telemetry/services/TelemetryService';
import type {
  TelemetryDeviceSeries,
  TelemetryResolution,
} from '@/features/telemetry/services/schemas';

/**
 * The series query's result, with the resolution/window it was actually fetched with
 * bundled alongside the data — not just the API's own `{ result }` shape.
 *
 * This bundling is what makes `placeholderData: keepPreviousData` safe to read from
 * directly: while a new range is loading, `data` stays the exact same object from the
 * last successful fetch (not a new one), so `data.resolution`/`data.from`/`data.to`
 * can never describe a *different* fetch than `data.result` does. A consumer reading
 * "the requested resolution" separately (e.g. from route search params) and pairing it
 * with `data.result` would get this wrong the instant the user picks a new range —
 * `data.result` is still the *old* window's data, fetched at the *old* resolution, so
 * its bucket keys (`_avg`/`_last` suffixed or not) only match that old resolution.
 */
export interface TelemetrySeriesResult {
  result: TelemetryDeviceSeries[];
  resolution: TelemetryResolution;
  from: Date;
  to: Date;
}

export const telemetryQueryKeys = {
  all: ['telemetry'] as const,
  fields: (systemId: number) => [...telemetryQueryKeys.all, 'fields', systemId] as const,
  series: (systemId: number, params: TelemetryQueryParams) =>
    [...telemetryQueryKeys.all, 'series', systemId, params] as const,
  emsStatus: (systemId: number) => [...telemetryQueryKeys.all, 'ems-status', systemId] as const,
};

export const telemetryQueries = {
  fields: (systemId: number) =>
    queryOptions({
      queryKey: telemetryQueryKeys.fields(systemId),
      queryFn: () => TelemetryService.getFields(systemId),
      // Small, stable catalog — shared by every card on the page, refetch is wasted effort.
      staleTime: 5 * 60_000,
    }),

  series: (systemId: number, params: TelemetryQueryParams) =>
    queryOptions({
      queryKey: telemetryQueryKeys.series(systemId, params),
      queryFn: async (): Promise<TelemetrySeriesResult> => {
        const { result } = await TelemetryService.getTelemetry(systemId, params);
        return { result, resolution: params.resolution, from: params.from, to: params.to };
      },
      // Keeps the previous window's charts on screen (dimmed by the UI) while a new
      // range loads, instead of collapsing the whole grid to a skeleton. Because
      // `resolution`/`from`/`to` are bundled into this same object, `data` is always
      // a self-consistent pairing — consumers never need a separate "displayed range"
      // snapshot to avoid mismatching new range info with still-loading old data.
      placeholderData: keepPreviousData,
    }),

  emsStatus: (systemId: number) =>
    queryOptions({
      queryKey: telemetryQueryKeys.emsStatus(systemId),
      queryFn: () => EmsStatusService.getStatus(systemId),
    }),
};
